import React, { useState, useRef, useEffect, forwardRef } from 'react';
import '../styles/TextInput.css';

const TextInput = forwardRef(({ onSubmit, initialText, timeZones }, ref) => {
  const [text, setText] = useState(initialText || '');
  const [copySuccess, setCopySuccess] = useState(false);
  const [customTimeZone, setCustomTimeZone] = useState('');
  const [customTimeZoneLabel, setCustomTimeZoneLabel] = useState('');
  const [customTimeZoneFormat, setCustomTimeZoneFormat] = useState('12h');
  const [selectedTimeZone, setSelectedTimeZone] = useState('none');
  const textareaRef = useRef(null);

  // Forward the ref to parent component
  useEffect(() => {
    if (ref) {
      ref.current = {
        querySelector: (selector) => {
          // If the selector is for textarea, return our internal ref
          if (selector === 'textarea') {
            return textareaRef.current;
          }
          // Otherwise, forward to the DOM element
          return textareaRef.current?.parentElement.querySelector(selector);
        }
      };
    }
  }, [ref]);

  // Update text if initialText changes (from parent component)
  useEffect(() => {
    if (initialText && initialText !== text) {
      setText(initialText);
    }
  }, [initialText]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (text.trim()) {
      onSubmit(text);
      // No longer clearing the text after submission
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);
  };

  // Copy text to clipboard
  const handleCopy = () => {
    if (textareaRef.current) {
      textareaRef.current.select();
      document.execCommand('copy');
      // Alternatively for modern browsers:
      // navigator.clipboard.writeText(text);
      
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  // Function to insert formatting at cursor position or around selected text
  const insertFormatting = (prefix, suffix = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = text.substring(start, end);
    const beforeText = text.substring(0, start);
    const afterText = text.substring(end);

    // If text is selected, wrap it with formatting
    // If no text is selected, just insert the formatting codes
    const newText = selectedText 
      ? `${beforeText}${prefix}${selectedText}${suffix}${afterText}`
      : `${beforeText}${prefix}${suffix}${afterText}`;

    setText(newText);

    // Focus back on textarea after button click
    setTimeout(() => {
      textarea.focus();
      
      // Set cursor position after the inserted formatting
      const newCursorPos = selectedText 
        ? start + prefix.length + selectedText.length + suffix.length
        : start + prefix.length;
      
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Handler for inserting predefined time zone widget
  const handleInsertTimeZone = (e) => {
    const value = e.target.value;
    setSelectedTimeZone(value);
    
    if (value === 'none') return;

    // Find the selected time zone object
    const timeZone = timeZones.find(tz => tz.identifier === value);
    if (!timeZone) return;

    // Generate the time zone widget code
    const timeCode = `|time[${timeZone.identifier},${timeZone.name},12h]|`;
    
    // Insert the time code
    insertFormatting(timeCode, '');
    
    // Reset the dropdown
    setTimeout(() => {
      setSelectedTimeZone('none');
    }, 100);
  };

  // Handler for inserting custom time zone
  const handleInsertCustomTimeZone = () => {
    if (!customTimeZone) return;

    try {
      // Test if the time zone is valid by attempting to format a date with it
      new Intl.DateTimeFormat('en-US', { timeZone: customTimeZone }).format(new Date());
      
      // Generate the time zone widget code
      const label = customTimeZoneLabel || customTimeZone;
      const timeCode = `|time[${customTimeZone},${label},${customTimeZoneFormat}]|`;
      
      // Insert the time code
      insertFormatting(timeCode, '');
      
      // Reset custom inputs
      setCustomTimeZone('');
      setCustomTimeZoneLabel('');
    } catch (error) {
      // Show an alert for invalid time zone
      alert('Invalid time zone identifier. Please use a valid format like "America/New_York" or "Europe/London".');
    }
  };

  // Formatting button configurations
  const formattingButtons = [
    { label: 'Bold', action: () => insertFormatting('*', '*'), icon: 'Bold' },
    { label: 'Italic', action: () => insertFormatting('_', '_'), icon: 'Italic' },
    { label: 'Blink', action: () => insertFormatting('~', '~'), icon: 'Blink' },
    { label: 'Color', action: () => insertFormatting('#red[', ']'), icon: 'Color' },
    { label: 'Large', action: () => insertFormatting('^large[', ']'), icon: 'Large' },
    { label: 'Small', action: () => insertFormatting('^small[', ']'), icon: 'Small' },
    { label: 'Highlight', action: () => insertFormatting('|h[', ']|'), icon: 'Highlight' },
    { label: 'Pulse', action: () => insertFormatting('|pulse[', ']|'), icon: 'Pulse' },
    { label: 'Divider', action: () => insertFormatting('|div|'), icon: 'Divider' },
    { label: 'Space', action: () => insertFormatting('|space|'), icon: 'Space' },
    { label: 'Image', action: () => insertFormatting('!img[', ']'), icon: 'Image' },
  ];

  // Emoji button configurations
  const emojiButtons = [
    { label: 'Smile', action: () => insertFormatting(':smile:'), emoji: '😊' },
    { label: 'Heart', action: () => insertFormatting(':heart:'), emoji: '❤️' },
    { label: 'Star', action: () => insertFormatting(':star:'), emoji: '⭐' },
    { label: 'Warning', action: () => insertFormatting(':warning:'), emoji: '⚠️' },
    { label: 'Fire', action: () => insertFormatting(':fire:'), emoji: '🔥' },
    { label: 'Thumbs Up', action: () => insertFormatting(':thumbsup:'), emoji: '👍' },
    { label: 'Clock', action: () => insertFormatting(':clock:'), emoji: '🕒' },
    { label: 'News', action: () => insertFormatting(':news:'), emoji: '📰' },
    { label: 'Mic', action: () => insertFormatting(':mic:'), emoji: '🎤' },
    { label: 'TV', action: () => insertFormatting(':tv:'), emoji: '📺' },
  ];

  return (
    <div className="text-input-container">
      <form onSubmit={handleSubmit}>
        <label htmlFor="scrolling-text">Enter text to scroll:</label>
        <textarea
          id="scrolling-text"
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          placeholder="Type your text here. Use formatting codes like *bold*, _italic_, ~blink~, or use the buttons below."
        />
        
        <div className="textarea-actions">
          <button 
            type="button" 
            className="copy-btn" 
            onClick={handleCopy}
            title="Copy text to clipboard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            {copySuccess ? 'Copied!' : 'Copy Text'}
          </button>
        </div>
        
        <div className="formatting-toolbar">
          <div className="toolbar-section">
            <h4>Text Formatting</h4>
            <div className="button-group">
              {formattingButtons.map((btn, index) => (
                <button
                  key={index}
                  type="button"
                  className="format-btn"
                  onClick={btn.action}
                  title={btn.label}
                >
                  {btn.icon}
                </button>
              ))}
            </div>
          </div>
          
          <div className="toolbar-section">
            <h4>Emojis</h4>
            <div className="button-group">
              {emojiButtons.map((btn, index) => (
                <button
                  key={index}
                  type="button"
                  className="emoji-btn"
                  onClick={btn.action}
                  title={btn.label}
                >
                  {btn.emoji}
                </button>
              ))}
            </div>
          </div>
          
          <div className="toolbar-section world-time-section">
            <h4>World Time</h4>
            
            <div className="world-time-controls">
              <div className="world-time-row">
                <select 
                  value={selectedTimeZone}
                  onChange={handleInsertTimeZone}
                  className="world-time-select"
                >
                  <option value="none">-- Select a Common Time Zone --</option>
                  {timeZones && timeZones.map(tz => (
                    <option key={tz.identifier} value={tz.identifier}>
                      {tz.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="world-time-row custom-time-inputs">
                <input 
                  type="text" 
                  placeholder="Time Zone ID (e.g., Asia/Tokyo)"
                  value={customTimeZone}
                  onChange={(e) => setCustomTimeZone(e.target.value)}
                  className="custom-tz-input"
                />
                
                <input 
                  type="text" 
                  placeholder="Display Label"
                  value={customTimeZoneLabel}
                  onChange={(e) => setCustomTimeZoneLabel(e.target.value)}
                  className="custom-tz-input"
                />
                
                <div className="format-selector">
                  <label className="format-radio">
                    <input 
                      type="radio" 
                      checked={customTimeZoneFormat === '12h'} 
                      onChange={() => setCustomTimeZoneFormat('12h')}
                      name="timeFormat"
                    />
                    12h
                  </label>
                  <label className="format-radio">
                    <input 
                      type="radio" 
                      checked={customTimeZoneFormat === '24h'} 
                      onChange={() => setCustomTimeZoneFormat('24h')}
                      name="timeFormat"
                    />
                    24h
                  </label>
                  
                  <button
                    type="button"
                    onClick={handleInsertCustomTimeZone}
                    className="add-time-btn"
                    title="Add Custom Time Zone"
                  >
                    Add
                  </button>
                </div>
              </div>
              
              <div className="world-time-help">
                <a 
                  href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="time-zone-link"
                >
                  View all time zone IDs
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <button type="submit" className="submit-btn">Update Scrolling Text</button>
      </form>
    </div>
  );
});

export default TextInput;